import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function SubmitVideoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    videoTitle: '',
    videoUrl: '',
    platform: '',
    gameType: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUrlGuide, setShowUrlGuide] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      navigate('/auth/login');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: profile } = await supabase
        .from('creator_profiles')
        .select('name')
        .eq('id', user.id)
        .single();

      const { error: submitError } = await supabase
        .from('video_submissions')
        .insert({
          creator_id: user.id,
          creator_name: profile?.name || 'Unknown',
          video_title: formData.videoTitle,
          video_url: formData.videoUrl,
          platform: formData.platform,
          game_type: formData.gameType,
          description: formData.description,
          status: 'pending',
          view_count: 0,
          reward_amount: 0,
        });

      if (submitError) throw submitError;

      const { data: currentProfile } = await supabase
        .from('creator_profiles')
        .select('total_videos')
        .eq('id', user.id)
        .single();

      if (currentProfile) {
        await supabase
          .from('creator_profiles')
          .update({
            total_videos: (currentProfile.total_videos || 0) + 1,
            updated_at: new Date().toISOString(),
          })
          .eq('id', user.id);
      }

      // Facebook Pixel Lead event
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'Lead');
        console.log('\u2705 Facebook Lead \u4e8b\u4ef6\u5df2\u89f8\u767c');
      } else {
        console.warn('\u26a0\ufe0f Facebook Pixel \u672a\u8f09\u5165');
      }

      setShowSuccess(true);
      setTimeout(() => {
        navigate('/creator/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error submitting video:', err);
      setError('\u63d0\u4ea4\u5931\u6557\uff0c\u8acb\u7a0d\u5f8c\u518d\u8a66');
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getPlatformIcon = (platform: string) => {
    const icons: Record<string, { icon: string; color: string; bg: string }> = {
      instagram: { icon: 'ri-instagram-line', color: 'text-pink-400', bg: 'bg-pink-500/20' },
      tiktok: { icon: 'ri-tiktok-line', color: 'text-white', bg: 'bg-slate-700' },
      youtube: { icon: 'ri-youtube-line', color: 'text-red-400', bg: 'bg-red-500/20' },
    };
    return icons[platform] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">

      {/* ===== 手機版 APP Header ===== */}
      <header className="md:hidden bg-slate-900/95 backdrop-blur-lg border-b border-slate-800 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate('/creator/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 hover:bg-slate-700 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl text-white"></i>
          </button>
          <h1 className="text-white font-bold text-base">提交影片</h1>
          <div className="w-9"></div>
        </div>
      </header>

      {/* ===== 電腦版 Header（不變） ===== */}
      <header className="hidden md:block bg-slate-900/50 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/43e5ec71-732c-47e6-8652-53c813fd533d.png"
              alt="Logo"
              className="h-10"
            />
          </Link>
          <Link
            to="/creator/dashboard"
            className="text-slate-400 hover:text-teal-400 text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-1"></i>
            返回控制台
          </Link>
        </div>
      </header>

      {/* ===== 手機版表單 ===== */}
      <div className="md:hidden px-4 py-5 pb-10">
        {/* 頂部提示 */}
        <div className="flex items-center gap-3 bg-teal-500/10 border border-teal-500/30 rounded-xl p-3.5 mb-5">
          <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-full flex-shrink-0">
            <i className="ri-film-line text-xl text-teal-400"></i>
          </div>
          <div>
            <p className="text-teal-300 text-sm font-medium">提交影片獲得獎金</p>
            <p className="text-slate-400 text-xs">審核通過即可獲得 NT$1,000</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3.5 flex items-start">
              <i className="ri-error-warning-line text-lg text-red-400 mr-2.5 mt-0.5"></i>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* 影片標題 */}
          <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-4">
            <label className="flex items-center gap-2 text-white font-medium text-sm mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-text text-sm text-teal-400"></i>
              </div>
              影片標題 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="videoTitle"
              value={formData.videoTitle}
              onChange={handleChange}
              required
              className="w-full px-3.5 py-3 bg-slate-900/80 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors text-sm"
              placeholder="例如：推筒子玩法完整教學"
            />
          </div>

          {/* 影片連結 */}
          <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-4">
            <label className="flex items-center gap-2 text-white font-medium text-sm mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-link text-sm text-teal-400"></i>
              </div>
              影片連結 <span className="text-red-400">*</span>
            </label>
            <input
              type="url"
              name="videoUrl"
              value={formData.videoUrl}
              onChange={handleChange}
              required
              className="w-full px-3.5 py-3 bg-slate-900/80 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors text-sm"
              placeholder="https://..."
            />
            <button
              type="button"
              onClick={() => setShowUrlGuide(!showUrlGuide)}
              className="mt-2.5 text-teal-400 hover:text-teal-300 text-xs flex items-center transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={`mr-1 ${showUrlGuide ? 'ri-arrow-up-s-line' : 'ri-question-line'}`}></i>
              {showUrlGuide ? '隱藏教學' : '如何複製影片連結？'}
            </button>

            {showUrlGuide && (
              <div className="mt-3 bg-slate-900/60 border border-slate-700 rounded-lg p-4 space-y-3">
                <h3 className="text-white font-medium text-xs flex items-center">
                  <i className="ri-lightbulb-line text-base text-yellow-400 mr-1.5"></i>
                  影片連結複製教學
                </h3>

                {[
                  { name: 'YouTube', icon: 'ri-youtube-line', color: 'text-red-400', bg: 'bg-red-500/20', example: 'https://www.youtube.com/watch?v=xxxxx', steps: ['開啟 YouTube 影片頁面', '點擊「分享」按鈕', '點擊「複製」即可'] },
                  { name: 'Instagram', icon: 'ri-instagram-line', color: 'text-white', bg: 'bg-gradient-to-br from-purple-500 to-pink-500', example: 'https://www.instagram.com/p/xxxxx/', steps: ['開啟 Instagram 貼文或 Reels', '點擊右上角「⋯」', '選擇「複製連結」'] },
                  { name: 'TikTok', icon: 'ri-tiktok-line', color: 'text-white', bg: 'bg-slate-900', example: 'https://www.tiktok.com/@user/video/xxxxx', steps: ['開啟 TikTok 影片', '點擊「分享」圖示', '選擇「複製連結」'] },
                ].map((p, i) => (
                  <div key={p.name} className={`space-y-1.5 ${i > 0 ? 'pt-3 border-t border-slate-700' : ''}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 flex items-center justify-center ${p.bg} rounded`}>
                        <i className={`${p.icon} text-sm ${p.color}`}></i>
                      </div>
                      <span className="text-white font-medium text-xs">{p.name}</span>
                    </div>
                    <ol className="text-slate-300 text-xs space-y-1 ml-8 list-decimal">
                      {p.steps.map((s) => <li key={s}>{s}</li>)}
                    </ol>
                    <div className="ml-8 bg-slate-800/50 rounded px-2.5 py-1.5 text-slate-500 text-xs font-mono truncate">
                      {p.example}
                    </div>
                  </div>
                ))}

                <div className="pt-3 border-t border-slate-700">
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2.5">
                    <div className="flex items-start gap-1.5">
                      <i className="ri-error-warning-line text-yellow-400 text-xs mt-0.5"></i>
                      <ul className="text-yellow-200/80 text-xs space-y-0.5 list-disc ml-3">
                        <li>確保影片為公開狀態</li>
                        <li>使用完整網址（含 https://）</li>
                        <li>請勿使用短網址</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 平台 & 遊戲類型 - 雙欄 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-4">
              <label className="flex items-center gap-2 text-white font-medium text-sm mb-3">
                <div className="w-7 h-7 flex items-center justify-center bg-teal-500/20 rounded-lg">
                  <i className="ri-apps-line text-sm text-teal-400"></i>
                </div>
                平台 <span className="text-red-400">*</span>
              </label>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                required
                className="w-full px-3 py-3 bg-slate-900/80 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors text-sm cursor-pointer"
              >
                <option value="">選擇平台</option>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="youtube">YouTube</option>
              </select>
              {formData.platform && (
                <div className="mt-2.5 flex items-center gap-2">
                  {(() => {
                    const p = getPlatformIcon(formData.platform);
                    if (!p) return null;
                    return (
                      <div className={`w-7 h-7 flex items-center justify-center ${p.bg} rounded-lg`}>
                        <i className={`${p.icon} text-sm ${p.color}`}></i>
                      </div>
                    );
                  })()}
                  <span className="text-slate-300 text-xs capitalize">{formData.platform}</span>
                </div>
              )}
            </div>

            <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-4">
              <label className="flex items-center gap-2 text-white font-medium text-sm mb-3">
                <div className="w-7 h-7 flex items-center justify-center bg-teal-500/20 rounded-lg">
                  <i className="ri-gamepad-line text-sm text-teal-400"></i>
                </div>
                遊戲 <span className="text-red-400">*</span>
              </label>
              <select
                name="gameType"
                value={formData.gameType}
                onChange={handleChange}
                required
                className="w-full px-3 py-3 bg-slate-900/80 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors text-sm cursor-pointer"
              >
                <option value="">選擇類型</option>
                <option value="推筒子">推筒子</option>
                <option value="百家樂">百家樂</option>
                <option value="其他牌類遊戲">其他牌類</option>
              </select>
            </div>
          </div>

          {/* 影片描述 */}
          <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-4">
            <label className="flex items-center gap-2 text-white font-medium text-sm mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-file-text-line text-sm text-teal-400"></i>
              </div>
              影片描述 <span className="text-red-400">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              maxLength={500}
              className="w-full px-3.5 py-3 bg-slate-900/80 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors resize-none text-sm"
              placeholder="簡單描述影片內容..."
            ></textarea>
            <div className="flex items-center justify-between mt-1.5">
              <p className="text-slate-500 text-xs">{formData.description.length}/500</p>
              {formData.description.length > 450 && (
                <p className="text-yellow-400 text-xs">即將達到上限</p>
              )}
            </div>
          </div>

          {/* 提交須知 */}
          <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-4">
            <h3 className="text-slate-300 font-medium mb-2.5 text-xs flex items-center">
              <i className="ri-information-line text-base text-teal-400 mr-1.5"></i>
              提交須知
            </h3>
            <ul className="text-slate-400 text-xs space-y-1.5 ml-5 list-disc">
              <li>影片需完整呈現遊戲玩法與規則說明</li>
              <li>審核通過後將獲得 NT$1,000 完成獎金</li>
              <li>多平台發布可獲得額外加碼獎金</li>
            </ul>
          </div>

          {/* 提交按鈕 */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 text-white py-3.5 rounded-xl font-bold text-base transition-all duration-300 shadow-lg shadow-teal-500/20 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                提交中...
              </>
            ) : (
              <>
                <i className="ri-upload-2-line text-lg"></i>
                提交影片
              </>
            )}
          </button>
        </form>
      </div>

      {/* ===== 電腦版表單（不變） ===== */}
      <div className="hidden md:block max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">提交新影片</h1>
          <p className="text-slate-400">填寫影片資訊，提交審核並獲得獎金</p>
        </div>

        <div className="bg-slate-800/80 rounded-xl p-8 border border-slate-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-start">
                <i className="ri-error-warning-line text-xl text-red-400 mr-3 mt-0.5"></i>
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="videoTitle" className="block text-white font-medium mb-2 text-sm">
                影片標題 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="videoTitle"
                name="videoTitle"
                value={formData.videoTitle}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors text-sm"
                placeholder="例如：推筒子玩法完整教學"
              />
            </div>

            <div>
              <label htmlFor="videoUrl" className="block text-white font-medium mb-2 text-sm">
                影片連結 <span className="text-red-400">*</span>
              </label>
              <input
                type="url"
                id="videoUrl"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors text-sm"
                placeholder="https://..."
              />

              <button
                type="button"
                onClick={() => setShowUrlGuide(!showUrlGuide)}
                className="mt-2 text-teal-400 hover:text-teal-300 text-xs flex items-center transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`ri-question-line mr-1 ${showUrlGuide ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                {showUrlGuide ? '隱藏教學' : '如何複製影片連結？'}
              </button>

              {showUrlGuide && (
                <div className="mt-4 bg-slate-900/50 border border-slate-700 rounded-lg p-5 space-y-4">
                  <h3 className="text-white font-medium text-sm flex items-center mb-3">
                    <i className="ri-lightbulb-line text-lg text-yellow-400 mr-2"></i>
                    影片連結複製教學
                  </h3>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-red-500/20 rounded">
                        <i className="ri-youtube-line text-lg text-red-400"></i>
                      </div>
                      <h4 className="text-white font-medium text-sm">YouTube</h4>
                    </div>
                    <ol className="text-slate-300 text-xs space-y-1.5 ml-10 list-decimal">
                      <li>開啟 YouTube 影片頁面</li>
                      <li>點擊影片下方的「分享」按鈕</li>
                      <li>點擊「複製」按鈕，即可複製連結</li>
                      <li>或直接複製瀏覽器網址列的連結</li>
                    </ol>
                    <div className="ml-10 bg-slate-800/50 rounded px-3 py-2 text-slate-400 text-xs font-mono">
                      範例：https://www.youtube.com/watch?v=xxxxx
                    </div>
                  </div>

                  <div className="space-y-2 pt-3 border-t border-slate-700">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded">
                        <i className="ri-instagram-line text-lg text-white"></i>
                      </div>
                      <h4 className="text-white font-medium text-sm">Instagram</h4>
                    </div>
                    <ol className="text-slate-300 text-xs space-y-1.5 ml-10 list-decimal">
                      <li>開啟 Instagram 貼文或 Reels</li>
                      <li>點擊右上角的「⋯」（更多選項）</li>
                      <li>選擇「複製連結」</li>
                      <li>或在電腦版直接複製瀏覽器網址列的連結</li>
                    </ol>
                    <div className="ml-10 bg-slate-800/50 rounded px-3 py-2 text-slate-400 text-xs font-mono">
                      範例：https://www.instagram.com/p/xxxxx/
                    </div>
                  </div>

                  <div className="space-y-2 pt-3 border-t border-slate-700">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-slate-900 rounded">
                        <i className="ri-tiktok-line text-lg text-white"></i>
                      </div>
                      <h4 className="text-white font-medium text-sm">TikTok</h4>
                    </div>
                    <ol className="text-slate-300 text-xs space-y-1.5 ml-10 list-decimal">
                      <li>開啟 TikTok 影片</li>
                      <li>點擊右側的「分享」圖示（箭頭）</li>
                      <li>選擇「複製連結」</li>
                      <li>或在電腦版直接複製瀏覽器網址列的連結</li>
                    </ol>
                    <div className="ml-10 bg-slate-800/50 rounded px-3 py-2 text-slate-400 text-xs font-mono">
                      範例：https://www.tiktok.com/@user/video/xxxxx
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-700">
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <i className="ri-error-warning-line text-yellow-400 text-sm mt-0.5"></i>
                        <div className="flex-1">
                          <p className="text-yellow-300 text-xs font-medium mb-1">重要提示</p>
                          <ul className="text-yellow-200/80 text-xs space-y-1 list-disc ml-4">
                            <li>請確保影片為公開狀態，否則無法審核</li>
                            <li>連結必須是完整的網址（包含 https://）</li>
                            <li>請勿使用短網址，使用原始連結</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="desktopPlatform" className="block text-white font-medium mb-2 text-sm">
                發布平台 <span className="text-red-400">*</span>
              </label>
              <select
                id="desktopPlatform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors text-sm cursor-pointer"
              >
                <option value="">請選擇平台</option>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="youtube">YouTube</option>
              </select>
            </div>

            <div>
              <label htmlFor="desktopGameType" className="block text-white font-medium mb-2 text-sm">
                遊戲類型 <span className="text-red-400">*</span>
              </label>
              <select
                id="desktopGameType"
                name="gameType"
                value={formData.gameType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors text-sm cursor-pointer"
              >
                <option value="">請選擇遊戲類型</option>
                <option value="推筒子">推筒子</option>
                <option value="百家樂">百家樂</option>
                <option value="其他牌類遊戲">其他牌類遊戲</option>
              </select>
            </div>

            <div>
              <label htmlFor="desktopDescription" className="block text-white font-medium mb-2 text-sm">
                影片描述 <span className="text-red-400">*</span>
              </label>
              <textarea
                id="desktopDescription"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors resize-none text-sm"
                placeholder="簡單描述影片內容，例如：詳細說明推筒子的基本規則、牌型判定方式，以及如何觀察走勢..."
              ></textarea>
              <p className="text-slate-400 text-xs mt-1">{formData.description.length}/500 字元</p>
            </div>

            <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
              <h3 className="text-teal-300 font-medium mb-2 text-sm flex items-center">
                <i className="ri-information-line text-lg mr-2"></i>
                提交須知
              </h3>
              <ul className="text-slate-300 text-xs space-y-1 ml-6 list-disc">
                <li>影片需完整呈現遊戲玩法與規則說明</li>
                <li>審核通過後將獲得 NT$1,000 完成獎金</li>
                <li>多平台發布可獲得額外加碼獎金</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 text-white py-4 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
            >
              {loading ? '提交中...' : '提交影片'}
            </button>
          </form>
        </div>
      </div>

      {/* 提交成功彈窗 */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-sm border border-slate-700 text-center animate-[fadeIn_0.3s_ease]">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-teal-500/20 rounded-full">
              <i className="ri-check-line text-4xl text-teal-400"></i>
            </div>
            <h3 className="text-white text-lg font-bold mb-2">提交成功！</h3>
            <p className="text-slate-400 text-sm">影片已送出審核，請耐心等待</p>
          </div>
        </div>
      )}
    </div>
  );
}
